// api/apiService.ts
import axios from 'axios';
import store from '../../redux/store';
import { logOut } from '../../redux/auth/authSlice';
import { notify } from '../helper/notify';

const apiClient = axios.create({
    baseURL: (import.meta as any).env.VITE_API_URL,
    headers: {
        'Content-Type': 'application/json',
    },

});

apiClient.interceptors.request.use(config => {
    if (!config.headers) {
        config.headers = {};
    }
    
    // Get token from multiple possible locations
    let token = '';
    try {
        const userData = JSON.parse(localStorage.getItem('user') || '{}');
        token = userData.token || 
                localStorage.getItem('token') || 
                localStorage.getItem('authToken') || '';
    } catch (e) {
        token = localStorage.getItem('token') || 
                localStorage.getItem('authToken') || '';
    }
    
    config.headers.Authorization = `Bearer ${token}`;
    return config;
});

apiClient.interceptors.response.use(
    response => response,
    error => {
        // Cancelled requests (e.g. session-expiry cancel) aren't real API errors.
        if (axios.isCancel(error)) {
            return Promise.reject(error);
        }
        // A 401 from the login request itself means "wrong credentials", NOT an
        // expired session — so don't log out / redirect (that reads as a page
        // refresh and swallows the API message). Let it reject so the caller can
        // surface the backend message inline.
        const requestUrl = error?.config?.url || '';
        const isLoginRequest = requestUrl.includes('/auth/login');
        if (error?.response?.status === 401 && !isLoginRequest) {
            store.dispatch(logOut());
            localStorage.removeItem('user');
            const lang = localStorage.getItem('i18nextLng') || 'en-us';
            const loginPath = lang === 'sv-se' ? '/e-postinloggning' : '/emaillogin';
            window.location.replace(`${loginPath}?lang=${lang}`);
            return Promise.reject(error);
        }
        // Surface real failures as an error alert so they're never silent, but
        // ONLY for server errors (5xx) and network failures (no response). 4xx
        // (validation / not-found) are typically handled inline by the caller,
        // so we don't hijack those with a global alert. `notify` de-dupes
        // identical messages, so a caller showing its own alert with the same
        // API message won't double up.
        const status = error?.response?.status;
        const isServerError = typeof status === 'number' && status >= 500;
        const isNetworkError = !error?.response; // request made but no response
        if (isServerError || isNetworkError) {
            const message =
                error?.response?.data?.message ||
                error?.message ||
                'Something went wrong';
            notify.error(message);
        }
        return Promise.reject(error);
    }
);


export const get = async (url: string, params?: Record<string, any>) => {
    try {
        const res = await apiClient.get(url, { params });
        return res.data;
    } catch (error) {
        throw error;
    }
};

export async function post<T>(url: string, data?: any, config?: { signal?: AbortSignal }): Promise<T> {
    try {
        const response = await apiClient.post<T>(url, data, config);
        return response.data;
    } catch (error: any) {
        // If backend returned an error response with data, throw that
        if (error.response && error.response.data) {
            throw error;
        }
        throw error;
    }
}

export async function put<T>(url: string, data: any): Promise<T> {
    const response = await apiClient.put<T>(url, data);
    return response.data;
}

export async function del<T>(url: string, data?: any): Promise<T> {
    const response = await apiClient.delete<T>(url, { data });
    return response.data;
}

export const getBlob = async (url: string, params?: Record<string, any>): Promise<Blob> => {
    const res = await apiClient.get(url, { params, responseType: 'blob' });
    return res.data;
};

export const postBlob = async (url: string, data?: any, params?: Record<string, any>): Promise<Blob> => {
    const res = await apiClient.post(url, data, { params, responseType: 'blob' });
    return res.data;
};